import * as React from "react";

import {GuideStateApi} from "@repo/guide-state";
import {useLocation} from "@repo/urls";

import type {ResolvedContentPath} from "../types/types.ts";
import useStableContentPath from "../ui/use-stable-content-path.ts";

import {isEmptyContentPath} from "./use-content-path.ts";

export type ContentPathContextType = {
    contentPath: ResolvedContentPath | null | undefined;
    previousContentPath: ResolvedContentPath | null | undefined;
};

export const ContentPathContext: React.Context<
    ContentPathContextType | null | undefined
> = React.createContext<ContentPathContextType | null | undefined>(null);
ContentPathContext.displayName = "ContentPathContext";

type Props = {
    children: React.ReactNode;
    contentPath: ResolvedContentPath | null | undefined;
};

export default function ContentPathProvider({
    children,
    contentPath,
}: Props): React.ReactElement {
    const previousContentPathRef = React.useRef<
        ResolvedContentPath | null | undefined
    >(null);
    const mostRecentContentPathRef = React.useRef<
        ResolvedContentPath | null | undefined
    >(null);
    const stableContentPath = useStableContentPath(contentPath);
    const {pathname} = useLocation();

    // Keep the Guide context up to date with our resolved content path
    React.useEffect(() => {
        GuideStateApi.set("navigation", (navigation) => ({
            ...navigation,
            contentPath: stableContentPath,
        }));
        if (!!stableContentPath && !isEmptyContentPath(stableContentPath)) {
            GuideStateApi.set("contentURLPath", pathname); // we need this because we can't get from the stableContentPath to the human-readable URL path to the same content.
        } else {
            GuideStateApi.set("contentURLPath", null);
        }
    }, [pathname, stableContentPath]);

    if (mostRecentContentPathRef.current !== stableContentPath) {
        previousContentPathRef.current = mostRecentContentPathRef.current;
        mostRecentContentPathRef.current = contentPath;
    }

    return (
        <ContentPathContext.Provider
            value={{
                contentPath: stableContentPath,
                previousContentPath: previousContentPathRef.current,
            }}
        >
            {children}
        </ContentPathContext.Provider>
    );
}
